import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertTriangle, CalendarClock, Flame, ShieldAlert } from "lucide-react";

const WINDOWS = [30, 60, 90, 180];
const fmtRs = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const ALERTS = {
  expired: { label: "EXPIRED", cls: "bg-red-600 text-white hover:bg-red-600", icon: ShieldAlert },
  critical: { label: "CRITICAL", cls: "bg-red-100 text-red-700 hover:bg-red-100", icon: Flame },
  high: { label: "HIGH ALERT", cls: "bg-amber-100 text-amber-800 hover:bg-amber-100", icon: AlertTriangle },
  warning: { label: "WARNING", cls: "bg-yellow-50 text-yellow-700 hover:bg-yellow-50", icon: CalendarClock },
};

export default function ExpireSoon() {
  const [days, setDays] = useState(90);
  const [items, setItems] = useState(null);

  useEffect(() => {
    setItems(null);
    api.get("/reports/expiring", { params: { days } }).then(({ data }) => setItems(data));
  }, [days]);

  const counts = (items || []).reduce((acc, m) => {
    acc[m.alert] = (acc[m.alert] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-5" data-testid="expire-soon-page">
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2">
          <AlertTriangle className="w-6 h-6 text-amber-600" /> Expire Soon — High Alert
        </h1>
        <p className="text-sm text-slate-500">Medicines nearing expiry — sell them before they expire.</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {WINDOWS.map((d) => (
          <button
            key={d}
            data-testid={`expire-window-${d}`}
            onClick={() => setDays(d)}
            className={`px-4 h-10 rounded-lg text-sm font-semibold border transition-all ${days === d ? "bg-emerald-900 text-white border-emerald-900" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-400"}`}
          >
            {d} days
          </button>
        ))}
      </div>

      {items === null ? (
        <div className="text-slate-500">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {Object.entries(ALERTS).map(([key, a]) => (
              <Card key={key} className="p-4 border-slate-200" data-testid={`expire-count-${key}`}>
                <div className="flex items-center gap-2">
                  <a.icon className={`w-4 h-4 ${key === "expired" || key === "critical" ? "text-red-600" : key === "high" ? "text-amber-600" : "text-yellow-600"}`} />
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{a.label}</span>
                </div>
                <div className="font-mono-num text-2xl font-extrabold text-slate-900 mt-1">{counts[key] || 0}</div>
              </Card>
            ))}
          </div>

          <Card className="border-slate-200">
            <div className="overflow-x-auto">
              <Table data-testid="expire-table">
                <TableHeader><TableRow>
                  <TableHead>Alert</TableHead><TableHead>Medicine</TableHead><TableHead>Batch</TableHead>
                  <TableHead>Expiry</TableHead><TableHead className="text-right">Days Left</TableHead>
                  <TableHead className="text-right">Stock</TableHead><TableHead className="text-right">Stock Value</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {items.map((m) => {
                    const a = ALERTS[m.alert];
                    return (
                      <TableRow key={m.id} data-testid={`expire-row-${m.id}`} className={m.alert === "expired" ? "bg-red-50/60" : m.alert === "critical" ? "bg-red-50/30" : ""}>
                        <TableCell><Badge className={a.cls}>{a.label}</Badge></TableCell>
                        <TableCell>
                          <div className="font-semibold text-slate-900">{m.name}</div>
                          <div className="text-xs text-slate-500">{m.manufacturer}</div>
                        </TableCell>
                        <TableCell className="text-sm">{m.batch_no || "—"}</TableCell>
                        <TableCell className="font-mono-num text-sm">{m.expiry_date}</TableCell>
                        <TableCell className={`text-right font-mono-num font-bold ${m.days_left < 0 ? "text-red-700" : m.days_left <= 30 ? "text-red-600" : m.days_left <= 60 ? "text-amber-700" : "text-slate-700"}`}>
                          {m.days_left < 0 ? `Expired ${Math.abs(m.days_left)} days ago` : `${m.days_left} days`}
                        </TableCell>
                        <TableCell className="text-right font-mono-num">{m.stock_strips} + {m.stock_loose_tablets} loose</TableCell>
                        <TableCell className="text-right font-mono-num text-slate-700">{fmtRs(m.stock_value)}</TableCell>
                      </TableRow>
                    );
                  })}
                  {items.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">
                      No medicines expiring in the next {days} days. All good!                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>

          <Card className="p-4 border-amber-200 bg-amber-50/60">
            <p className="text-xs text-amber-800 leading-relaxed">
              <span className="font-semibold">Tip:</span> Prioritize selling CRITICAL and HIGH ALERT medicines at the counter.
              Selling EXPIRED medicines is illegal — return them to the supplier or dispose of them.
            </p>
          </Card>
        </>
      )}
    </div>
  );
}
